/**
 * SyllabusHeader — 강의 작업 화면의 머리 한 줄(설계 FE-20260912 §W01·§W05).
 *
 *   [←] 강의 이름 [강의 바꾸기 ▾] ……………… [공유 상태 ▾] [⋯]
 *
 * 옛 화면은 강의를 열면 강의 정보 카드(이름·설명·공유 셋·사본·지우기)가 오른쪽을 통째로 차지했다. 강의에 들어온 사람의 일은
 * 줄을 고르고 문제를 찾는 것이라, 공유·정보는 작은 버튼 뒤의 떠 있는 판으로 보냈다 — 열기 전에는 본문 자리를 쓰지 않는다(수용 기준 1).
 * 판은 한 번에 하나만 연다(FloatingPanel 끼리 겹치면 서로의 바깥 클릭 규칙이 서로를 닫는다).
 *
 * 쓰기는 부모가 넘긴 함수(onShare·onUpdateHeader·onCopy·onDelete)로만 한다 — 대기열·revision 은 작업 상태 훅(useSyllabusWorkspace)의 일이다.
 * 각 판은 그 약속이 끝날 때까지 누름을 잠그고, 실패하면 판 안에 사유를 한 줄 적는다. 약속이 거절되지 않으면 성공으로 본다.
 * 강의 바꾸기 목록만 여기서 직접 부른다(listSyllabi·createSyllabus) — 지금 연 강의의 상태와 무관한 읽기라서.
 *
 * props
 *   data     — 헤더(syllabus_id, name, description, author_name, is_mine, permission, share_scope, node_count, revision) (+ items 가 있으면 줄 수에 쓴다)
 *   owner    — 주인인가(공유 변경·이름 편집·지우기가 열린다)
 *   busy     — 판 안 저장 단추를 잠근다(부모가 다른 쓰기를 기다리는 동안 등)
 *   onBack() · onSwitch(syllabusId) · onCreated(syllabusId) — id 는 문자열로 넘긴다
 *   onShare(scope) → Promise      — null | 'managers' | 'all'
 *   onUpdateHeader({ name, description }) → Promise
 *   onCopy({ withDraft }) → Promise<{ copyId, applied, reason }>
 *   onDelete() → Promise
 *   canCopyDraft — 지금 고른 줄에 조건 초안이 있는가(사본에 반영 선택지)
 *   getAccessToken — 강의 바꾸기 목록 조회용
 *   sheet    — 좁은 화면이면 판을 하단 시트로(설계 §W08)
 *   className — 바깥 여백 등(부모 칸의 머리 줄 모양은 부모가 정한다)
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, MoreHorizontal } from 'lucide-react';
import SyllabusSwitcher from './SyllabusSwitcher';
import SharePopover, { shareLabel } from './SharePopover';
import SyllabusMoreMenu from './SyllabusMoreMenu';

const ringCls = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';
// 원형 손잡이 — 나무의 펼침·더보기 손잡이와 같은 값(SyllabusTree · FolderTree)
const handleCls = `shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 ${ringCls}`;
// 공유 상태 — 옛 머리 배지(Syllabus.js)의 작은 회색 배지 규격을 누를 수 있게만 바꿨다(설계 「공유 상태는 기존 gray100/gray500 작은 배지」)
const shareBtnCls = `inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700 ${ringCls}`;

function SyllabusHeader({
  data = null,
  owner = false,
  busy = false,
  onBack,
  onSwitch,
  onCreated,
  onShare,
  onUpdateHeader,
  onCopy,
  onDelete,
  canCopyDraft = false,
  copyRecovery = null,
  onRetryCopyRecovery,
  onDiscardCopyRecovery,
  getAccessToken,
  sheet = false,
  className = '',
}) {
  const [open, setOpen] = useState(null); // null | 'switch' | 'share' | 'more'
  const switchRef = useRef(null);
  const shareRef = useRef(null);
  const moreRef = useRef(null);
  const sid = data?.syllabus_id == null ? null : String(data.syllabus_id);

  // 강의가 바뀌면 열린 판을 닫는다 — 앞 강의의 공유·이름을 새 강의 위에 띄우지 않게
  useEffect(() => { setOpen(null); }, [sid]);

  // 같은 버튼을 다시 누르면 닫는다. 다른 버튼은 FloatingPanel 의 바깥 mousedown 이 먼저 닫고, 이 click 이 새 판을 연다
  const toggle = (which) => setOpen((cur) => (cur === which ? null : which));
  const close = () => setOpen(null);
  const openSyllabus = (id) => { setOpen(null); onSwitch?.(id); };

  const shareText = owner ? shareLabel(data?.share_scope) : '공유받음';

  return (
    <div className={`flex min-w-0 items-center gap-1.5 ${className}`}>
      <button type="button" onClick={() => onBack?.()} aria-label="강의 목록으로" title="강의 목록으로" className={handleCls}>
        <ArrowLeft className="size-4" />
      </button>
      {/* 이름이 곧 [강의 바꾸기] — 나무 칸(256~440px)의 한 줄을 네 조각이 나누면 이름이 서너 글자로 잘린다(공유받은 강의 실측).
          「강의 바꾸기」 글자는 화면에서 빼고 읽는 이름에만 둔다. 강의를 못 읽어도(지워짐·권한 없음) 다른 강의로 갈 수 있게 data 없이도 둔다 */}
      <h2 className="min-w-0 flex-1 text-sm font-bold text-gray-900">
        <button
          ref={switchRef}
          type="button"
          onClick={() => toggle('switch')}
          aria-haspopup="dialog"
          aria-expanded={open === 'switch'}
          aria-label={`강의 바꾸기 — 지금 ${data?.name || '강의'}`}
          title={`${data?.name || '강의'} — 눌러서 강의 바꾸기`}
          className={`inline-flex max-w-full items-center gap-0.5 rounded-lg px-1 py-0.5 text-left transition-colors hover:bg-gray-100 ${ringCls}`}
        >
          <span className="min-w-0 truncate">{data?.name || '강의'}</span>
          <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
        </button>
      </h2>
      {data && (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <button
            ref={shareRef}
            type="button"
            onClick={() => toggle('share')}
            aria-haspopup="dialog"
            aria-expanded={open === 'share'}
            aria-label={owner ? `공유 범위 — ${shareText}` : `공유받음 — ${shareLabel(data.share_scope)}`}
            className={shareBtnCls}
          >
            {shareText} <ChevronDown className="size-3" />
          </button>
          <button
            ref={moreRef}
            type="button"
            onClick={() => toggle('more')}
            aria-haspopup="dialog"
            aria-expanded={open === 'more'}
            aria-label="강의 더보기"
            className={handleCls}
          >
            <MoreHorizontal className="size-4" />
          </button>
        </div>
      )}

      {open === 'switch' && (
        <SyllabusSwitcher
          anchorRef={switchRef}
          currentId={sid}
          getAccessToken={getAccessToken}
          onSwitch={openSyllabus}
          onCreated={(id) => { setOpen(null); onCreated?.(id); }}
          onClose={close}
          sheet={sheet}
        />
      )}
      {open === 'share' && data && (
        <SharePopover anchorRef={shareRef} data={data} owner={owner} busy={busy} onShare={onShare} onClose={close} sheet={sheet} />
      )}
      {open === 'more' && data && (
        <SyllabusMoreMenu
          anchorRef={moreRef}
          data={data}
          owner={owner}
          busy={busy}
          canCopyDraft={canCopyDraft}
          copyRecovery={copyRecovery}
          onRetryCopyRecovery={onRetryCopyRecovery}
          onDiscardCopyRecovery={onDiscardCopyRecovery}
          onUpdateHeader={onUpdateHeader}
          onCopy={onCopy}
          onDelete={onDelete}
          onOpenCopy={openSyllabus}
          onClose={close}
          sheet={sheet}
        />
      )}
    </div>
  );
}

export default SyllabusHeader;
